import { PhotoLocationsMap } from "@/components/inspection/photo-locations-map";
import { Button } from "@/components/ui/button";
import {
  type ClockSyncState,
  type GeoPosition,
  PHOTO_TIME_SYNC_MAX_AGE_MS,
  type PhotoAnswer,
  type ServerTimeResponse,
  buildPhotoAnswer,
  captureFrameToJpegBlob,
  clockSyncAgeMs,
  getCurrentGeoPosition,
  hasPhotoAnswers,
  isClockSyncFresh,
  normalizePhotoAnswers,
  openCameraStream,
  photoBlobToFile,
  serializePhotoAnswers,
  syncClockWithServer,
} from "@/lib/photo-answer";
import { Camera, Check, Clock, MapPin, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { toast } from "sonner";

type PropertyLocation = {
  latitude: number;
  longitude: number;
};

type PhotoCaptureFieldProps = {
  value: unknown;
  onChange: (value: PhotoAnswer | PhotoAnswer[] | null) => void;
  /**
   * Optional completion signal: single-photo after "Use photo"; multi-photo
   * after "Done". Perform workflow does not auto-advance on this callback —
   * inspectors stay on the confirmation/map view until they tap Next.
   */
  onCaptured?: (value: PhotoAnswer | PhotoAnswer[]) => void;
  uploadFile: (file: File) => Promise<string>;
  /**
   * Fetch trusted server time for clock sync. When provided, capture is gated
   * on a fresh sync and photo answers include time-integrity fields.
   */
  fetchServerTime?: () => Promise<ServerTimeResponse>;
  allowMultiple?: boolean;
  disabled?: boolean;
  /** Property lat/lng for clustering photo pins near the property. */
  propertyLocation?: PropertyLocation | null;
};

type LocationStatus =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; position: GeoPosition }
  | { kind: "error"; message: string };

type CameraStatus =
  | { kind: "idle" }
  | { kind: "starting" }
  | { kind: "ready" }
  | { kind: "error"; message: string };

type TimeSyncStatus =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; sync: ClockSyncState }
  | { kind: "error"; message: string };

type PendingCapture = {
  previewUrl: string;
  blob: Blob;
  position: GeoPosition;
  capturedAt: Date;
  sync: ClockSyncState | null;
};

type Phase = "camera" | "confirm" | "gallery";

/**
 * Live camera viewer for Photo questions with confirm/retake UX.
 * Capture is blocked until geolocation and (when configured) a fresh server
 * clock sync are available; saved answer is file token + lat/lng + timestamp +
 * timezone + time-integrity fields.
 */
export function PhotoCaptureField({
  value,
  onChange,
  onCaptured,
  uploadFile,
  fetchServerTime,
  allowMultiple = false,
  disabled = false,
  propertyLocation = null,
}: PhotoCaptureFieldProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const previewId = useId();
  const requireTimeSync = typeof fetchServerTime === "function";

  const savedPhotos = normalizePhotoAnswers(value);
  const [phase, setPhase] = useState<Phase>(() =>
    hasPhotoAnswers(value) ? "gallery" : "camera",
  );
  const [pending, setPending] = useState<PendingCapture | null>(null);
  const [locationStatus, setLocationStatus] = useState<LocationStatus>({
    kind: "idle",
  });
  const [cameraStatus, setCameraStatus] = useState<CameraStatus>({
    kind: "idle",
  });
  const [timeSyncStatus, setTimeSyncStatus] = useState<TimeSyncStatus>({
    kind: "idle",
  });
  const [capturing, setCapturing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const locationReady = locationStatus.kind === "ready";
  const cameraReady = cameraStatus.kind === "ready";
  const timeSyncReady =
    !requireTimeSync ||
    (timeSyncStatus.kind === "ready" && isClockSyncFresh(timeSyncStatus.sync));
  const canCapture =
    !disabled &&
    !capturing &&
    !confirming &&
    locationReady &&
    cameraReady &&
    timeSyncReady &&
    phase === "camera";

  const stopCamera = useCallback(() => {
    for (const track of streamRef.current?.getTracks() ?? []) {
      track.stop();
    }
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const clearPending = useCallback(() => {
    setPending((prev) => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      return null;
    });
  }, []);

  const requestLocation = useCallback(async () => {
    setLocationStatus({ kind: "loading" });
    try {
      const position = await getCurrentGeoPosition();
      setLocationStatus({ kind: "ready", position });
    } catch (error) {
      setLocationStatus({
        kind: "error",
        message:
          error instanceof Error
            ? error.message
            : "Unable to get your location.",
      });
    }
  }, []);

  const requestTimeSync = useCallback(async () => {
    if (!fetchServerTime) {
      setTimeSyncStatus({ kind: "idle" });
      return;
    }
    setTimeSyncStatus({ kind: "loading" });
    try {
      const sync = await syncClockWithServer(fetchServerTime);
      setTimeSyncStatus({ kind: "ready", sync });
    } catch (error) {
      setTimeSyncStatus({
        kind: "error",
        message:
          error instanceof Error
            ? error.message
            : "Unable to synchronize time with the server.",
      });
    }
  }, [fetchServerTime]);

  const startGenerationRef = useRef(0);
  const hadPhotosRef = useRef(hasPhotoAnswers(value));

  const startCamera = useCallback(async () => {
    const generation = ++startGenerationRef.current;
    setCameraStatus({ kind: "starting" });
    stopCamera();
    try {
      const stream = await openCameraStream();
      if (generation !== startGenerationRef.current) {
        for (const track of stream.getTracks()) {
          track.stop();
        }
        return;
      }
      const video = videoRef.current;
      if (!video) {
        // Preview unmounted (phase change / remount race) — release immediately.
        for (const track of stream.getTracks()) {
          track.stop();
        }
        throw new Error("Camera preview element is missing.");
      }
      streamRef.current = stream;
      video.srcObject = stream;
      await video.play();
      if (generation !== startGenerationRef.current) {
        stopCamera();
        return;
      }
      setCameraStatus({ kind: "ready" });
    } catch (error) {
      if (generation !== startGenerationRef.current) return;
      stopCamera();
      setCameraStatus({
        kind: "error",
        message:
          error instanceof Error
            ? error.message
            : "Unable to access the camera.",
      });
    }
  }, [stopCamera]);

  // Only leave gallery when the controlled value *transitions* from having
  // photos to empty (next question / clear). Do not react to the brief window
  // after "Use photo" where phase is already gallery but the parent has not
  // committed the new value yet.
  useEffect(() => {
    const hasPhotos = hasPhotoAnswers(value);
    if (hadPhotosRef.current && !hasPhotos && phase === "gallery") {
      setPhase("camera");
    }
    hadPhotosRef.current = hasPhotos;
  }, [value, phase]);

  // Location + clock sync are independent of the MediaStream lifecycle.
  useEffect(() => {
    if (phase !== "camera" || disabled) return;
    void requestLocation();
    void requestTimeSync();
  }, [disabled, phase, requestLocation, requestTimeSync]);

  // Start/stop camera only when the live preview phase is active. Invalidate
  // in-flight starts on cleanup so a stale getUserMedia result cannot attach
  // after unmount or after navigating to the next question.
  useEffect(() => {
    if (phase !== "camera" || disabled) {
      startGenerationRef.current += 1;
      stopCamera();
      return;
    }
    void startCamera();
    return () => {
      startGenerationRef.current += 1;
      stopCamera();
    };
  }, [disabled, phase, startCamera, stopCamera]);

  useEffect(() => {
    return () => {
      clearPending();
      stopCamera();
    };
  }, [clearPending, stopCamera]);

  const ensureFreshTimeSync = async (): Promise<ClockSyncState | null> => {
    if (!requireTimeSync || !fetchServerTime) return null;
    if (
      timeSyncStatus.kind === "ready" &&
      isClockSyncFresh(timeSyncStatus.sync)
    ) {
      return timeSyncStatus.sync;
    }
    const sync = await syncClockWithServer(fetchServerTime);
    setTimeSyncStatus({ kind: "ready", sync });
    return sync;
  };

  const handleCapture = async () => {
    if (!canCapture || locationStatus.kind !== "ready") return;
    const video = videoRef.current;
    if (!video) return;

    setCapturing(true);
    try {
      let sync: ClockSyncState | null = null;
      try {
        sync = await ensureFreshTimeSync();
      } catch (error) {
        setTimeSyncStatus({
          kind: "error",
          message:
            error instanceof Error
              ? error.message
              : "Unable to synchronize time with the server.",
        });
        toast.error("Accurate time required", {
          description:
            "Could not sync with the server clock. Check your connection and try again.",
        });
        return;
      }
      if (requireTimeSync && !sync) {
        toast.error("Accurate time required", {
          description: "Wait for time synchronization before capturing.",
        });
        return;
      }

      let position = locationStatus.position;
      try {
        position = await getCurrentGeoPosition();
        setLocationStatus({ kind: "ready", position });
      } catch (error) {
        setLocationStatus({
          kind: "error",
          message:
            error instanceof Error
              ? error.message
              : "Unable to get your location.",
        });
        toast.error("Location required", {
          description:
            "Enable location services to capture a photo for this question.",
        });
        return;
      }

      const capturedAt = new Date();
      const blob = await captureFrameToJpegBlob(video);
      const previewUrl = URL.createObjectURL(blob);
      clearPending();
      setPending({ previewUrl, blob, position, capturedAt, sync });
      stopCamera();
      setCameraStatus({ kind: "idle" });
      setPhase("confirm");
    } catch (error) {
      toast.error("Photo capture failed", {
        description:
          error instanceof Error ? error.message : "Please try again",
      });
    } finally {
      setCapturing(false);
    }
  };

  const handleRetakePending = () => {
    clearPending();
    setPhase("camera");
  };

  const handleUsePhoto = async () => {
    if (!pending || confirming) return;
    setConfirming(true);
    try {
      const file = photoBlobToFile(pending.blob, pending.capturedAt);
      const token = await uploadFile(file);
      if (!token) {
        throw new Error("Upload failed: no token returned");
      }
      const integrity =
        pending.sync != null
          ? {
              clock_offset_ms: pending.sync.clock_offset_ms,
              time_sync_age_ms: clockSyncAgeMs(
                pending.sync,
                pending.capturedAt.getTime(),
              ),
              position_timestamp_ms: pending.position.position_timestamp_ms,
            }
          : {
              clock_offset_ms: 0,
              time_sync_age_ms: 0,
              position_timestamp_ms: pending.position.position_timestamp_ms,
            };
      const answer = buildPhotoAnswer(
        token,
        pending.position,
        pending.capturedAt,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        requireTimeSync || pending.sync != null ? integrity : null,
      );
      clearPending();

      if (allowMultiple) {
        const next = [...savedPhotos, answer];
        const serialized = serializePhotoAnswers(next, true);
        onChange(serialized);
        setPhase("gallery");
        toast.success("Photo added");
      } else {
        onChange(answer);
        setPhase("gallery");
        toast.success("Photo saved");
        onCaptured?.(answer);
      }
    } catch (error) {
      toast.error("Photo save failed", {
        description:
          error instanceof Error ? error.message : "Please try again",
      });
    } finally {
      setConfirming(false);
    }
  };

  const handleTakeAnother = () => {
    setPhase("camera");
  };

  const handleDone = () => {
    if (savedPhotos.length === 0) return;
    const serialized = serializePhotoAnswers(savedPhotos, allowMultiple);
    if (!serialized) return;
    onCaptured?.(serialized);
  };

  const handleClearAndRetake = () => {
    onChange(null);
    clearPending();
    setPhase("camera");
  };

  if (phase === "confirm" && pending) {
    return (
      <div className="space-y-3">
        <div className="relative overflow-hidden rounded-lg bg-black aspect-[4/3]">
          <img
            src={pending.previewUrl}
            alt="Captured preview"
            className="h-full w-full object-cover"
          />
        </div>
        <p className="text-sm text-neutral-600">
          {pending.position.latitude.toFixed(5)},{" "}
          {pending.position.longitude.toFixed(5)}
          {pending.position.accuracy_meters != null
            ? ` (±${Math.round(pending.position.accuracy_meters)} m)`
            : ""}
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            type="button"
            className="w-full sm:flex-1"
            onClick={() => void handleUsePhoto()}
            disabled={disabled || confirming}
          >
            <Check className="mr-2 h-4 w-4" />
            {confirming ? "Saving…" : "Use photo"}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full sm:flex-1"
            onClick={handleRetakePending}
            disabled={disabled || confirming}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Retake
          </Button>
        </div>
      </div>
    );
  }

  if (phase === "gallery" && savedPhotos.length > 0) {
    return (
      <div className="space-y-3">
        <div className="space-y-1">
          <p className="text-sm font-medium text-neutral-900">
            {allowMultiple
              ? `${savedPhotos.length} photo${savedPhotos.length === 1 ? "" : "s"} saved`
              : "Photo saved"}
          </p>
          <PhotoLocationsMap
            photos={savedPhotos}
            propertyLocation={propertyLocation}
            compact
          />
        </div>
        {allowMultiple ? (
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              className="w-full sm:flex-1"
              onClick={handleTakeAnother}
              disabled={disabled}
            >
              <Camera className="mr-2 h-4 w-4" />
              Take another photo
            </Button>
            {onCaptured ? (
              <Button
                type="button"
                variant="secondary"
                className="w-full sm:flex-1"
                onClick={handleDone}
                disabled={disabled}
              >
                Done
              </Button>
            ) : null}
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={handleClearAndRetake}
            disabled={disabled}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Retake photo
          </Button>
        )}
      </div>
    );
  }

  const waitingLabel = !locationReady
    ? "Waiting for location…"
    : requireTimeSync && !timeSyncReady
      ? "Waiting for time sync…"
      : "Capture photo";

  return (
    <div className="space-y-3">
      {locationStatus.kind === "error" ? (
        <div
          role="alert"
          className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950"
        >
          <p className="font-medium">Location required to capture a photo</p>
          <p className="mt-1">
            {locationStatus.message} Enable location permission for this site
            (and device location services), then try again.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => void requestLocation()}
            disabled={disabled}
          >
            <MapPin className="mr-2 h-4 w-4" />
            Enable location
          </Button>
        </div>
      ) : null}

      {requireTimeSync && timeSyncStatus.kind === "error" ? (
        <div
          role="alert"
          className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950"
        >
          <p className="font-medium">Accurate time required to capture</p>
          <p className="mt-1">
            {timeSyncStatus.message} Sync with the server clock, then try again.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => void requestTimeSync()}
            disabled={disabled}
          >
            <Clock className="mr-2 h-4 w-4" />
            Sync time
          </Button>
        </div>
      ) : null}

      {locationStatus.kind === "loading" ? (
        <p className="text-sm text-neutral-600">Getting your location…</p>
      ) : null}

      {requireTimeSync && timeSyncStatus.kind === "loading" ? (
        <p className="text-sm text-neutral-600">Synchronizing time…</p>
      ) : null}

      {locationStatus.kind === "ready" ? (
        <p className="flex items-center gap-1.5 text-sm text-neutral-600">
          <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
          Location ready ({locationStatus.position.latitude.toFixed(5)},{" "}
          {locationStatus.position.longitude.toFixed(5)}
          {locationStatus.position.accuracy_meters != null
            ? ` ±${Math.round(locationStatus.position.accuracy_meters)} m`
            : ""}
          )
        </p>
      ) : null}

      {requireTimeSync &&
      timeSyncStatus.kind === "ready" &&
      isClockSyncFresh(timeSyncStatus.sync) ? (
        <p className="flex items-center gap-1.5 text-sm text-neutral-600">
          <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
          Time synced
          {Math.abs(timeSyncStatus.sync.clock_offset_ms) >= 1000
            ? ` (offset ${Math.round(timeSyncStatus.sync.clock_offset_ms / 1000)}s)`
            : ""}
          {clockSyncAgeMs(timeSyncStatus.sync) > 15_000
            ? ` · ${Math.round(clockSyncAgeMs(timeSyncStatus.sync) / 1000)}s ago`
            : ""}
        </p>
      ) : null}

      {requireTimeSync &&
      timeSyncStatus.kind === "ready" &&
      !isClockSyncFresh(timeSyncStatus.sync) ? (
        <p className="text-sm text-amber-800">
          Time sync expired (max{" "}
          {Math.round(PHOTO_TIME_SYNC_MAX_AGE_MS / 60000)} min). Refreshing…
        </p>
      ) : null}

      <div className="relative overflow-hidden rounded-lg bg-black aspect-[4/3]">
        <video
          id={previewId}
          ref={videoRef}
          className="h-full w-full object-cover"
          playsInline
          muted
          autoPlay
        />
        {cameraStatus.kind === "starting" || cameraStatus.kind === "idle" ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-sm text-white">
            Starting camera…
          </div>
        ) : null}
        {cameraStatus.kind === "error" ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/80 p-4 text-center text-sm text-white">
            <p>{cameraStatus.message}</p>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => void startCamera()}
              disabled={disabled}
            >
              Retry camera
            </Button>
          </div>
        ) : null}
      </div>

      <Button
        type="button"
        className="w-full"
        onClick={() => void handleCapture()}
        disabled={!canCapture}
      >
        <Camera className="mr-2 h-4 w-4" />
        {capturing ? "Capturing…" : waitingLabel}
      </Button>
    </div>
  );
}
